import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { formatCurrency } from "@/lib/utils";
import { Plane, Plus, Trash2, AlertCircle } from "lucide-react";
import { FleetBookTable } from "./FleetBookTable";

const TYPE_NAMES = {
  turboprop: "Turboprop (ATR 72)",
  used_narrowbody: "Used A320",
  new_narrowbody: "New 737 MAX",
  widebody: "Wide-body 787",
};

export default function FleetSection({ decision, updateDecision, team }) {
  const { t } = useTranslation();
  const [aircraftTypes, setAircraftTypes] = useState([]);
  const [buyType, setBuyType] = useState("used_narrowbody");
  const [buyQty, setBuyQty] = useState(1);
  const [leaseType, setLeaseType] = useState("used_narrowbody");
  const [leaseQty, setLeaseQty] = useState(1);

  useEffect(() => {
    axios.get(`${API}/reference/aircraft-types`).then(res => setAircraftTypes(res.data)).catch(() => {});
  }, []);

  const aircraft = team?.aircraft || [];
  const ownedAircraft = aircraft.filter(a => a.owned);
  const sellList = decision.fleet_sell || [];
  const retireList = decision.fleet_retire || [];

  const addBuy = () => {
    const buys = [...(decision.fleet_buy || [])];
    const existing = buys.find(b => b.type === buyType);
    if (existing) existing.quantity += buyQty;
    else buys.push({ type: buyType, quantity: buyQty });
    updateDecision("fleet_buy", buys);
  };

  const removeBuy = (idx) => {
    const buys = [...(decision.fleet_buy || [])];
    buys.splice(idx, 1);
    updateDecision("fleet_buy", buys);
  };

  const addLease = () => {
    const leases = [...(decision.fleet_lease || [])];
    const existing = leases.find(l => l.type === leaseType);
    if (existing) existing.quantity += leaseQty;
    else leases.push({ type: leaseType, quantity: leaseQty });
    updateDecision("fleet_lease", leases);
  };

  const removeLease = (idx) => {
    const leases = [...(decision.fleet_lease || [])];
    leases.splice(idx, 1);
    updateDecision("fleet_lease", leases);
  };

  const toggleSell = (acId) => {
    const list = sellList.includes(acId) ? sellList.filter(id => id !== acId) : [...sellList, acId];
    updateDecision("fleet_sell", list);
  };

  const toggleRetire = (acId) => {
    const list = retireList.includes(acId) ? retireList.filter(id => id !== acId) : [...retireList, acId];
    updateDecision("fleet_retire", list);
  };

  return (
    <div className="space-y-6">
      {/* Current Fleet */}
      <Card>
        <CardHeader className="card-header-flight-deck">
          <CardTitle className="flex items-center gap-2"><Plane className="w-5 h-5" /> {t("decisions.currentFleetCount", { count: aircraft.length })}</CardTitle>
        </CardHeader>
        <CardContent className="pt-4">
          {aircraft.length === 0 ? (
            <p className="text-slate-500 text-sm text-center py-4">{t("decisions.noAircraftInFleet")}</p>
          ) : (
            <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[500px]">
              <thead>
                <tr className="border-b border-slate-100">
                  <th className="text-left py-2 px-3 text-xs font-semibold text-slate-500">ID</th>
                  <th className="text-left py-2 px-3 text-xs font-semibold text-slate-500">{t("decisions.aircraftType")}</th>
                  <th className="text-center py-2 px-3 text-xs font-semibold text-slate-500">{t("decisions.age")}</th>
                  <th className="text-center py-2 px-3 text-xs font-semibold text-slate-500">{t("decisions.qualityRating")}</th>
                  <th className="text-center py-2 px-3 text-xs font-semibold text-slate-500">{t("company.equity")}</th>
                  <th className="text-center py-2 px-3 text-xs font-semibold text-slate-500">Actions</th>
                </tr>
              </thead>
              <tbody>
                {aircraft.map(ac => (
                  <tr key={ac.id} className="border-b border-slate-50 hover:bg-slate-50">
                    <td className="py-2 px-3 font-mono text-xs">{ac.id}</td>
                    <td className="py-2 px-3">{TYPE_NAMES[ac.type] || ac.type}</td>
                    <td className="py-2 px-3 text-center font-mono">{Number(ac.age).toFixed(1)}y</td>
                    <td className="py-2 px-3 text-center">
                      <span className={`font-mono ${ac.condition >= 80 ? "text-emerald-600" : ac.condition >= 60 ? "text-amber-600" : "text-rose-600"}`}>{ac.condition}%</span>
                    </td>
                    <td className="py-2 px-3 text-center">
                      <span className={`px-2 py-0.5 rounded text-xs ${ac.owned ? "bg-sky-100 text-sky-700" : "bg-amber-100 text-amber-700"}`}>{ac.owned ? t("decisions.buyAircraft") : t("decisions.leaseAircraft")}</span>
                    </td>
                    <td className="py-2 px-3 text-center">
                      {ac.owned && (
                        <div className="flex justify-center gap-1">
                          <Button size="sm" variant={sellList.includes(ac.id) ? "destructive" : "outline"} className="h-7 text-xs" onClick={() => toggleSell(ac.id)} data-testid={`sell-ac-${ac.id}`}>
                            {sellList.includes(ac.id) ? t("decisions.sell") + "..." : t("decisions.sell")}
                          </Button>
                          <Button size="sm" variant={retireList.includes(ac.id) ? "destructive" : "outline"} className="h-7 text-xs" onClick={() => toggleRetire(ac.id)} data-testid={`retire-ac-${ac.id}`}>
                            {retireList.includes(ac.id) ? t("decisions.retire") + "..." : t("decisions.retire")}
                          </Button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          )}
        </CardContent>
      </Card>

      <FleetBookTable teamId={team?.id} refreshKey={aircraft.length} />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Buy Aircraft */}
        <Card>
          <CardHeader className="card-header-flight-deck">
            <CardTitle>{t("decisions.buyAircraft")}</CardTitle>
            <CardDescription>{t("decisions.buyAircraftDesc")}</CardDescription>
          </CardHeader>
          <CardContent className="pt-4 space-y-4">
            <div className="flex gap-2 items-end">
              <div className="flex-1">
                <Label className="text-xs">{t("decisions.aircraftType")}</Label>
                <Select value={buyType} onValueChange={setBuyType}>
                  <SelectTrigger data-testid="buy-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {aircraftTypes.map(t => (
                      <SelectItem key={t.code} value={t.code}>{t.name} - {formatCurrency(t.purchase_price)} ({t.seats} seats)</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="w-20">
                <Label className="text-xs">{t("decisions.quantity")}</Label>
                <Input type="number" min={1} max={5} value={buyQty} onChange={e => setBuyQty(parseInt(e.target.value) || 1)} className="font-mono" data-testid="buy-qty-input" />
              </div>
              <Button size="sm" onClick={addBuy} className="h-9" data-testid="add-buy-btn"><Plus className="w-4 h-4" /></Button>
            </div>
            {(decision.fleet_buy || []).map((b, i) => (
              <div key={`${b.type}-${i}`} className="flex items-center justify-between p-3 bg-sky-50 rounded-lg border border-sky-200">
                <span className="text-sm font-medium">{TYPE_NAMES[b.type] || b.type} x{b.quantity}</span>
                <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-rose-500" onClick={() => removeBuy(i)}><Trash2 className="w-3.5 h-3.5" /></Button>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Lease Aircraft */}
        <Card>
          <CardHeader className="card-header-flight-deck">
            <CardTitle>{t("decisions.leaseAircraft")}</CardTitle>
            <CardDescription>{t("decisions.leaseAircraftDesc")}</CardDescription>
          </CardHeader>
          <CardContent className="pt-4 space-y-4">
            <div className="flex gap-2 items-end">
              <div className="flex-1">
                <Label className="text-xs">{t("decisions.aircraftType")}</Label>
                <Select value={leaseType} onValueChange={setLeaseType}>
                  <SelectTrigger data-testid="lease-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {aircraftTypes.map(t => (
                      <SelectItem key={t.code} value={t.code}>{t.name} - {formatCurrency(t.lease_per_quarter)}/qtr ({t.seats} seats)</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="w-20">
                <Label className="text-xs">{t("decisions.quantity")}</Label>
                <Input type="number" min={1} max={5} value={leaseQty} onChange={e => setLeaseQty(parseInt(e.target.value) || 1)} className="font-mono" data-testid="lease-qty-input" />
              </div>
              <Button size="sm" onClick={addLease} className="h-9" data-testid="add-lease-btn"><Plus className="w-4 h-4" /></Button>
            </div>
            {(decision.fleet_lease || []).map((l, i) => (
              <div key={i} className="flex items-center justify-between p-3 bg-amber-50 rounded-lg border border-amber-200">
                <span className="text-sm font-medium">{TYPE_NAMES[l.type] || l.type} x{l.quantity}</span>
                <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-rose-500" onClick={() => removeLease(i)}><Trash2 className="w-3.5 h-3.5" /></Button>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>

      {/* Warnings */}
      {(sellList.length > 0 || retireList.length > 0) && (
        <div className="flex items-center gap-2 text-amber-600 bg-amber-50 px-4 py-2 rounded-lg">
          <AlertCircle className="w-4 h-4 shrink-0" />
          <span className="text-sm">
            {sellList.length > 0 && `Selling ${sellList.length} aircraft. `}
            {retireList.length > 0 && `Retiring ${retireList.length} aircraft (no sale proceeds).`}
          </span>
        </div>
      )}
    </div>
  );
}
